import { formatMonthName } from './DateUtils';
import type { IStockData } from '@/common/interfaces/stocks.interface';

export const getStockColorCodeCandidates = (
  apparelColorCode: string,
  apparelColorHex: string
): string[] => {
  const candidates: string[] = [];

  const pushUnique = (value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed || candidates.includes(trimmed)) {
      return;
    }
    candidates.push(trimmed);
  };

  [apparelColorCode, apparelColorHex].forEach((code) => {
    if (!code?.trim()) {
      return;
    }

    pushUnique(code);

    const withoutHash = code.replace(/^#/, '');
    pushUnique(withoutHash);

    if (!code.startsWith('#')) {
      pushUnique(`#${code}`);
    }
  });

  return candidates;
};

export const getMaxRestockDate = (data: IStockData[]) => {
  const validDates = data
    .filter((item) => item.restock_date !== null)
    .map((item) => new Date(item.restock_date));

  if (validDates.length === 0) {
    return null;
  }

  const maxDate = new Date(Math.max(...validDates.map((date) => date.getTime())));

  const day = maxDate.getDate();
  const month = maxDate.getMonth() + 1;

  return `${month}/${day}`;
};

export const formatListWithAmpersand = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} & ${items[1]}`;
  // Remove the period from the end of the string if it exists before joining
  const lastItem = items[items.length - 1].endsWith('.')
    ? items[items.length - 1].slice(0, -1)
    : items[items.length - 1];
  return `${items.slice(0, -1).join(', ')} & ${lastItem}`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  return `${month}/${day}`;
};

export const addDaysToDate = (isoDateString: string, daysToAdd: number) => {
  if (!isoDateString) {
    return {
      formattedDate: null,
      originalDate: null,
    };
  }

  const estDate = new Date(
    new Date(isoDateString).toLocaleString('en-US', {
      timeZone: 'America/New_York',
    })
  );

  const utcDate = new Date(
    Date.UTC(estDate.getFullYear(), estDate.getMonth(), estDate.getDate() + daysToAdd)
  );

  const day = utcDate.getUTCDate();
  const month = utcDate.getUTCMonth() + 1;

  return {
    formattedDate: `${month}/${day}`,
    originalDate: utcDate.toISOString(),
  };
};

export const formatDateToMonthDay = (isoDateString: string): string => {
  const date = new Date(isoDateString);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = monthNames[date.getMonth()];
  const day = date.getDate();
  return `${formatMonthName(monthName)} ${day}`;
};
